import React, { useState, useRef, useEffect, useContext } from 'react';
import { Briefcase, Trash2, Camera, Plus, RefreshCw, AlertTriangle, Zap, Target, BarChart3, CheckCircle, Wrench } from 'lucide-react';
import { AuthContext } from '../contexts/AuthContext';
import { BagService, InventoryService, LinksIDService } from '../services/api';
import { auth } from '../services/firebase';
import { BagCloudChart } from '../components/bag/BagCloudChart';
import { Card } from '../components/ds/Card';
import { Button } from '../components/ds/Button';
import OptimizationPanel from '../components/bag/OptimizationPanel';

export default function BagPage() {
  const [bag, setBag] = useState([]);
  const [newClub, setNewClub] = useState({ name: '', carry: 150, category: 'Iron', ball_id: null, loft: null, lie_angle: null });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [inventoryCache, setInventoryCache] = useState({ heads: [], shafts: [], balls: [] });
  const [showSearch, setShowSearch] = useState(false);
  const [golfBalls, setGolfBalls] = useState([]);
  const [userBalls, setUserBalls] = useState([]);
  const [gapAnalysis, setGapAnalysis] = useState({ gaps: [], warnings: [], recommendations: [], loft_conflicts: [], total_clubs: 0, coverage: { longest: 0, shortest: 0, avg_gap: 0 } });
  const [importedClubs, setImportedClubs] = useState([]);
  const [editingClub, setEditingClub] = useState(null);
  const [editForm, setEditForm] = useState({ loft: 0, lie: 'standard' });
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importSource, setImportSource] = useState('');
  const [loftSetting, setLoftSetting] = useState(0);
  const [optimizationData, setOptimizationData] = useState(null);
  const [selectedClubForOptimization, setSelectedClubForOptimization] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null); // 'synced', 'local', 'error'
  const [enrichmentInfo, setEnrichmentInfo] = useState(null);
  const importInputRef = useRef(null);
  const { user, passport, login } = useContext(AuthContext);

  const loadBag = async () => {
    if (!user) return;

    // For authenticated users, try LinksID first (source of truth)
    if (!user.isGuest && passport && auth?.currentUser) {
      try {
        const token = await auth.currentUser.getIdToken();
        const linksidBag = await LinksIDService.getBag(token);
        // Map LinksID bag format to PocketFitter display format
        const mapped = (linksidBag || []).map(slot => ({
          id: slot.id,
          club_name: slot.nickname || `${slot.head_manufacturer || ''} ${slot.head_model || ''}`.trim(),
          name: slot.nickname || `${slot.head_manufacturer || ''} ${slot.head_model || ''}`.trim(),
          category: slot.slot?.includes('iron') ? 'Iron' :
                    slot.slot?.includes('wood') ? 'Wood' :
                    slot.slot?.includes('hybrid') ? 'Hybrid' :
                    slot.slot === 'driver' ? 'Driver' :
                    slot.slot === 'putter' ? 'Putter' :
                    ['pw','gw','sw','lw'].includes(slot.slot) ? 'Wedge' : 'Iron',
          carry: slot.perf_carry_yards,
          loft: slot.head_loft_adjusted_deg || slot.head_loft_deg,
          lie_angle: slot.head_lie_deg,
          year: slot.head_model_year,
          shaft: slot.shaft_manufacturer && slot.shaft_model
            ? `${slot.shaft_manufacturer} ${slot.shaft_model}${slot.shaft_flex_raw ? ` (${slot.shaft_flex_raw})` : ''}`
            : null,
          grip: slot.grip_manufacturer && slot.grip_model
            ? `${slot.grip_manufacturer} ${slot.grip_model}${slot.grip_size ? ` ${slot.grip_size}` : ''}`
            : null,
          shaft_weight_g: slot.shaft_weight_g,
          total_weight_g: slot.total_weight_g,
          swing_weight: slot.swing_weight,
          equipment_xref_id: slot.equipment_xref_id || null,
          matched: !!slot.equipment_xref_id,
          source: 'linksid',
        }));
        setBag(mapped);
        setSyncStatus('synced');
        return;
      } catch (err) {
        console.warn('[LinksID] Bag read failed, falling back to local:', err.message);
        setSyncStatus('local');
      }
    }

    // Fallback: read from PocketFitter local
    BagService.getBag().then(items => {
      setBag(items);
      if (!user.isGuest) setSyncStatus('local');
    });
  };

  const loadGaps = () => {
    if (user) BagService.getGaps().then(data => setGapAnalysis({
      gaps: data.gaps || [],
      warnings: data.warnings || [],
      recommendations: data.recommendations || [],
      loft_conflicts: data.loft_conflicts || [],
      total_clubs: data.total_clubs || 0,
      coverage: data.coverage || { longest: 0, shortest: 0, avg_gap: 0 }
    })).catch(() => {});
  };

  const loadUserBalls = () => {
    if (user) BagService.getBalls().then(data => setUserBalls(data.balls || []));
  };

  const addBallToBag = async (ballId) => {
    try {
      await BagService.addBall(ballId);
      loadUserBalls();
    } catch (e) {
      alert('Error adding ball: ' + (e.response?.data?.detail || e.message));
    }
  };

  const removeBallFromBag = async (id) => {
    try {
      await BagService.removeBall(id);
      loadUserBalls();
    } catch (e) {
      alert('Error removing ball: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Load inventory for search
  const loadInventory = async () => {
    if (inventoryCache.heads.length > 0) return;
    try {
      const [headsRes, shaftsRes, ballsRes] = await Promise.all([
        InventoryService.getHeads(),
        InventoryService.getShafts(),
        InventoryService.getBalls()
      ]);
      setInventoryCache({
        heads: headsRes.items || [],
        shafts: shaftsRes.items || [],
        balls: ballsRes.balls || []
      });
      setGolfBalls(ballsRes.balls || []);
    } catch (e) { console.error('Failed to load inventory', e); }
  };

  useEffect(() => { loadBag(); loadGaps(); loadInventory(); loadUserBalls(); }, [user]);

  // Fetch enrichment data when editing a matched club
  useEffect(() => {
    setEnrichmentInfo(null);
    if (editingClub?.equipment_xref_id) {
      LinksIDService.getEquipmentInfo(editingClub.equipment_xref_id)
        .then(data => setEnrichmentInfo(data))
        .catch(() => setEnrichmentInfo(null));
    }
  }, [editingClub]);
  useEffect(() => { if (bag.length > 0) loadGaps(); }, [bag.length]);

  // Search inventory when query changes — tokenized multi-word + category filter
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const tokens = searchQuery.toLowerCase().split(/\s+/).filter(t => t);
    // Map frontend categories to DB categories for filtering
    const catMap = { 'Wood': 'Wood', 'Fairway': 'Wood' };
    const selectedCat = catMap[newClub.category] || newClub.category;

    const matchedHeads = inventoryCache.heads.filter(h => {
      // Show only RH to avoid duplicates (RH + LH for every head)
      if (h.dexterity && h.dexterity !== 'RH') return false;
      // Filter by selected category
      if (selectedCat && h.category !== selectedCat) return false;
      // Every token must match at least one field
      const searchable = [h.manufacturer, h.model_name, h.head_profile].filter(Boolean).join(' ').toLowerCase();
      return tokens.every(t => searchable.includes(t));
    }).slice(0, 10).map(h => ({
      type: 'head',
      name: `${h.manufacturer} ${h.model_name}`,
      category: h.category,
      detail: [
        h.loft ? `${h.loft}°` : null,
        h.head_profile,
        h.model_year ? `(${h.model_year})` : null,
        h.msrp ? `$${h.msrp}` : null,
      ].filter(Boolean).join(' · ')
    }));
    setSearchResults(matchedHeads);
  }, [searchQuery, inventoryCache, newClub.category]);

  const selectFromSearch = (item) => {
    setNewClub({ ...newClub, name: item.name, category: item.category === 'Fairway' ? 'Wood' : item.category });
    setSearchQuery('');
    setSearchResults([]);
    setShowSearch(false);
  };

  // Map PocketFitter category + name to LinksID slot name
  const toLinksIdSlot = (category, name) => {
    const n = (name || '').toLowerCase();
    if (category === 'Driver') return 'driver';
    if (category === 'Putter') return 'putter';
    if (category === 'Wedge') {
      if (n.includes('lob') || n.includes('lw') || n.includes('60')) return 'lw';
      if (n.includes('sand') || n.includes('sw') || n.includes('56')) return 'sw';
      if (n.includes('gap') || n.includes('gw') || n.includes('52') || n.includes('50')) return 'gw';
      return 'pw';
    }
    if (category === 'Hybrid') {
      const m = n.match(/(\d)/); return m ? `hybrid_${m[1]}` : 'hybrid_4';
    }
    if (category === 'Wood') {
      const m = n.match(/(\d)/); return m ? `${m[1]}_wood` : '3_wood';
    }
    // Iron — extract number
    const m = n.match(/(\d)/);
    if (m) return `iron_${m[1]}`;
    if (n.includes('pw') || n.includes('pitching')) return 'pw';
    return 'iron_7';
  };

  const addClub = async () => {
    if (!newClub.name) return;
    setLoading(true);
    try {
      if (syncStatus === 'synced' && auth?.currentUser) {
        const token = await auth.currentUser.getIdToken();
        // Parse name into manufacturer + model (split on first space)
        const parts = newClub.name.trim().split(/\s+/);
        const manufacturer = parts[0] || 'Unknown';
        const model = parts.slice(1).join(' ') || newClub.name;
        await LinksIDService.addClub(token, {
          slot: toLinksIdSlot(newClub.category, newClub.name),
          head_manufacturer: manufacturer,
          head_model: model,
          head_loft_deg: newClub.loft || undefined,
          nickname: newClub.name,
          source: 'pocketfitter',
        });
      } else {
        await BagService.addClub({...newClub, settings: {loft_sleeve: loftSetting}});
      }
      setNewClub({ name: '', carry: 150, category: 'Iron', ball_id: null, loft: null, lie_angle: null });
      setLoftSetting(0);
      loadBag();
      loadGaps();
    } catch (e) { alert('Error: ' + (e.response?.data?.detail || e.message)); }
    setLoading(false);
  };

  const deleteClub = async (id) => {
    if (syncStatus === 'synced' && auth?.currentUser) {
      const token = await auth.currentUser.getIdToken();
      await LinksIDService.deleteClub(token, id);
    } else {
      await BagService.deleteClub(id);
    }
    loadBag();
    loadGaps();
  };

  const updateClub = async (id, updates) => {
    try {
      if (syncStatus === 'synced' && auth?.currentUser) {
        const token = await auth.currentUser.getIdToken();
        // Map PocketFitter field names to LinksID field names
        const linksIdUpdates = {};
        if (updates.loft != null) linksIdUpdates.head_loft_adjusted_deg = updates.loft;
        if (updates.lie_angle != null) linksIdUpdates.head_lie_deg = updates.lie_angle === 'flat' ? -2 : updates.lie_angle === 'upright' ? 2 : 0;
        if (updates.carry_distance != null) linksIdUpdates.nickname = undefined; // carry not directly updatable via LinksID patch
        await LinksIDService.updateClub(token, id, linksIdUpdates);
      } else {
        await BagService.updateClub(id, updates);
      }
      loadBag();
      loadGaps();
    } catch (e) { alert('Update failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const clearBag = async () => {
    if (!window.confirm('Are you sure you want to remove all clubs from your bag?')) return;
    try {
      await BagService.clearBag();
      loadBag();
      loadGaps();
    } catch (e) { alert('Error clearing bag: ' + e.message); }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const data = await BagService.importBag(formData);
      if (data.status === 'success') {
        setImportedClubs(data.clubs.map(c => ({ ...c, include: true })));
        setImportSource(data.detected_source);
        setShowImportModal(true);
      }
    } catch (e) { alert('Import failed: ' + (e.response?.data?.detail || e.message)); }
    setImporting(false);
    if (importInputRef.current) importInputRef.current.value = '';
  };

  const confirmImport = async () => {
    const clubsToImport = importedClubs.filter(c => c.include).map(c => ({
      name: c.name,
      type: c.type,
      avg_distance: c.avg_distance,
      head_id: c.head_id,
      source: importSource
    }));
    if (clubsToImport.length === 0) return;
    setImporting(true);
    try {
      await BagService.confirmImport(clubsToImport);
      setShowImportModal(false);
      setImportedClubs([]);
      loadBag();
      loadGaps();
    } catch (e) { alert('Save failed: ' + e.message); }
    setImporting(false);
  };

  const updateImportedClub = (index, field, value) => {
    setImportedClubs(prev => prev.map((c, i) => i === index ? { ...c, [field]: value } : c));
  };
  
  const handleOptimize = async (clubId) => {
    try {
        const result = await BagService.optimizeClub(clubId);
        setOptimizationData(result);
        const club = bag.find(c => c.id === clubId);
        setSelectedClubForOptimization(club);
    } catch (error) {
        console.error("Optimization failed:", error);
        alert('Optimization failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleApplyOptimization = (recommendedLoft) => {
    if (!selectedClubForOptimization) return;
    
    // Create a deep copy of settings to avoid direct mutation
    const newSettings = JSON.parse(JSON.stringify(selectedClubForOptimization.settings || {}));
    newSettings.loft_sleeve = recommendedLoft;

    updateClub(selectedClubForOptimization.id, { settings: newSettings });
    setOptimizationData(null);
    setSelectedClubForOptimization(null);
};

  if (!user) return (
    <div className="max-w-4xl mx-auto text-center p-12">
      <Briefcase className="mx-auto text-text-muted mb-4" size={48} />
      <p className="text-text-secondary mb-4">Sign in to manage your bag</p>
      <Button variant="primary" size="lg" onClick={login}>Sign In</Button>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto space-y-8 animate-in fade-in">
        {optimizationData && (
            <OptimizationPanel
                isOpen={!!optimizationData}
                onClose={() => setOptimizationData(null)}
                clubData={selectedClubForOptimization}
                optimizationData={optimizationData}
                onApply={handleApplyOptimization}
            />
        )}
      {/* Hero Section */}
      <div className="text-center py-8 px-4">
        <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-text-primary mb-2">
          Your Golf Bag Setup
        </h1>
        <p className="text-text-secondary text-base max-w-2xl mx-auto">
          Manage your clubs, analyze distance gaps, and optimize your bag for peak performance.
        </p>
      </div>

      <Card className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-heading font-bold flex gap-2 items-center"><Briefcase className="text-primary" /> My Bag</h2>
          {bag.length > 0 && (
            <button onClick={clearBag} className="text-xs text-text-muted hover:text-status-error flex items-center gap-1 transition-colors"><Trash2 size={12}/> Clear All</button>
          )}
        </div>

        {/* Entry mode toggle */}
        <div className="flex gap-2 mb-4 flex-wrap">
          <button onClick={() => { setShowSearch(false); }} className={`px-3 py-1.5 rounded text-sm transition-colors ${!showSearch ? 'bg-primary text-text-primary' : 'bg-surface text-text-secondary hover:text-text-primary'}`}>Manual Entry</button>
          <button onClick={() => { setShowSearch(true); loadInventory(); }} className={`px-3 py-1.5 rounded text-sm transition-colors ${showSearch ? 'bg-primary text-text-primary' : 'bg-surface text-text-secondary hover:text-text-primary'}`}>Search Inventory</button>
          <label className="px-3 py-1.5 rounded text-sm bg-secondary hover:bg-secondary-light text-text-primary cursor-pointer flex items-center gap-1.5 transition-colors">
            <Camera size={14} /> {importing ? 'Importing...' : 'Import Screenshot'}
            <input type="file" ref={importInputRef} accept="image/*" onChange={handleImportFile} disabled={importing} className="hidden" />
          </label>
        </div>

        {showSearch ? (
          <div className="mb-6 relative">
            <input
              placeholder="Search inventory (e.g. Titleist, Callaway, TaylorMade...)"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full bg-background border border-border p-3 rounded-lg text-text-primary"
            />
            {searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-background border border-border rounded-lg shadow-xl max-h-64 overflow-y-auto">
                {searchResults.map((item, i) => (
                  <div key={i} onClick={() => selectFromSearch(item)} className="p-3 hover:bg-surface-light cursor-pointer border-b border-border last:border-0">
                    <div className="font-medium text-text-primary">{item.name}</div>
                    <div className="text-xs text-text-muted">{item.category} {item.detail && `· ${item.detail}`}</div>
                  </div>
                ))}
              </div>
            )}
            {searchQuery.length >= 2 && searchResults.length === 0 && inventoryCache.heads.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-background border border-border rounded-lg p-3 text-text-muted text-sm">No matches found</div>
            )}
            {newClub.name && (
              <div className="mt-3 p-3 bg-surface-light rounded-lg">
                <div className="text-xs text-text-muted mb-1">Selected:</div>
                <div className="text-text-primary font-medium">{newClub.name}</div>
              </div>
            )}
          </div>
        ) : null}

        <div className="space-y-3 mb-6">
          {/* First row: Name, Category, Carry */}
          <div className="flex gap-3 flex-wrap">
            {!showSearch && (
              <input placeholder="Club name (e.g. Driver, 7-Iron)" value={newClub.name} onChange={e => setNewClub({...newClub, name: e.target.value})} className="bg-background border border-border p-3 rounded-lg text-text-primary flex-1 min-w-[200px]" />
            )}
            <select value={newClub.category} onChange={e => setNewClub({...newClub, category: e.target.value})} className="bg-background border border-border p-3 rounded-lg text-text-primary">
              <option>Driver</option><option>Wood</option><option>Hybrid</option><option>Iron</option><option>Wedge</option><option>Putter</option>
            </select>
            <input type="number" placeholder="Carry (yds)" value={newClub.carry} onChange={e => setNewClub({...newClub, carry: e.target.value})} className="bg-background border border-border p-3 rounded-lg text-text-primary w-28" />
          </div>

          {/* Second row: Loft, Lie Angle, Loft Adjustment, Ball */}
          <div className="flex gap-3 flex-wrap">
            <input
              type="number"
              step="0.5"
              placeholder="Loft (°)"
              value={newClub.loft || ''}
              onChange={e => setNewClub({...newClub, loft: e.target.value ? parseFloat(e.target.value) : null})}
              className="bg-background border border-border p-3 rounded-lg text-text-primary w-24"
              title="Club loft in degrees"
            />
            <select
              value={newClub.lie_angle || ''}
              onChange={e => setNewClub({...newClub, lie_angle: e.target.value || null})}
              className="bg-background border border-border p-3 rounded-lg text-text-primary w-32"
              title="Lie angle adjustment"
            >
              <option value="">Lie Angle</option>
              <option value="flat">Flat (-2°)</option>
              <option value="standard">Standard</option>
              <option value="upright">Upright (+2°)</option>
            </select>
            <select value={loftSetting} onChange={(e) => setLoftSetting(parseFloat(e.target.value))} className="bg-background border border-border p-3 rounded-lg text-text-primary w-40" title="Adjustable hosel loft setting">
              <option value={-2}>Loft Adj. -2.0°</option>
              <option value={-1.5}>Loft Adj. -1.5°</option>
              <option value={0}>Loft Adj. STD</option>
              <option value={1.5}>Loft Adj. +1.5°</option>
              <option value={2}>Loft Adj. +2.0°</option>
            </select>
            <button onClick={addClub} disabled={loading || !newClub.name} className="bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 disabled:text-text-muted text-text-primary px-4 py-3 rounded-lg flex items-center gap-2">{loading ? <RefreshCw className="animate-spin" size={16}/> : <Plus size={16}/>} Add</button>
          </div>
        </div>

        <div className="space-y-2">
          {bag.length === 0 && <p className="text-text-muted text-center py-8">Your bag is empty. Add your clubs above.</p>}
          {bag.map(c => (
            <div key={c.id} className="bg-background p-4 rounded-lg border border-border">
              <div className="flex justify-between items-center">
                <div
                  className="flex items-center gap-4 flex-1 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => {
                    setEditingClub(c);
                    setEditForm({ loft: c.loft || 10.5, lie: 'standard' });
                  }}
                >
                  <span className="text-xs text-text-muted uppercase w-16">{c.category || 'Club'}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-text-primary">{c.name || 'Unknown Club'}</span>
                    {c.year && <span className="text-xs text-text-muted">({c.year})</span>}
                    {c.matched && (
                      <CheckCircle size={14} className="text-success" title="Matched with inventory database" />
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-xs text-text-muted">
                    {c.loft && <span className="font-mono">{c.loft}°</span>}
                    {c.lie_angle && (
                      <span className="font-mono">
                        {typeof c.lie_angle === 'number' ? `${c.lie_angle}°` :
                         c.lie_angle === 'standard' ? 'Std' : c.lie_angle === 'upright' ? '+2°' : '-2°'}
                      </span>
                    )}
                    {c.swing_weight && <span className="font-mono">{c.swing_weight}</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      value={c.carry || ''}
                      onChange={(e) => {
                        const newCarry = parseInt(e.target.value) || 0;
                        setBag(prev => prev.map(club => club.id === c.id ? { ...club, carry: newCarry } : club));
                      }}
                      onBlur={(e) => {
                        const newCarry = parseInt(e.target.value);
                        if (newCarry && newCarry !== c.carry) {
                          updateClub(c.id, { carry_distance: newCarry });
                        }
                      }}
                      className="w-16 bg-surface border border-border rounded px-2 py-1 text-right font-mono text-blue-400"
                    />
                    <span className="text-text-muted text-sm">yds</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingClub(c);
                      setEditForm({ loft: c.loft || 10.5, lie: 'standard' });
                    }}
                    className="text-text-muted hover:text-blue-400 p-1"
                    title="Edit club settings"
                  >
                    <Wrench size={16}/>
                  </button>
                  <button onClick={() => deleteClub(c.id)} className="text-text-muted hover:text-red-400 p-1"><Trash2 size={16}/></button>
                </div>
              </div>
              {/* Enriched specs row */}
              {(c.shaft || c.grip || c.swing_weight) && (
                <div className="mt-2 ml-20 flex flex-wrap gap-3 text-xs text-text-muted">
                  {c.shaft && <span title="Shaft">{c.shaft}{c.shaft_weight_g ? ` ${c.shaft_weight_g}g` : ''}</span>}
                  {c.grip && <span title="Grip">{c.grip}</span>}
                  {c.swing_weight && <span title="Swing Weight">SW: {c.swing_weight}</span>}
                  {c.total_weight_g && <span title="Total Weight">{c.total_weight_g}g total</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Golf Balls Section */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-heading font-bold flex gap-2 items-center">
            <Target className="text-yellow-500" /> Golf Balls in My Bag
          </h2>
        </div>

        {/* Add Ball Selector */}
        <div className="mb-6">
          <select
            onChange={(e) => {
              const ballId = parseInt(e.target.value);
              if (ballId) {
                addBallToBag(ballId);
                e.target.value = '';
              }
            }}
            className="w-full bg-background border border-border p-3 rounded-lg text-text-primary"
            defaultValue=""
          >
            <option value="" disabled>Select ball to add...</option>
            {golfBalls.map(ball => (
              <option key={ball.id} value={ball.id}>
                {ball.display_name} - {ball.driver_spin_profile} spin ({ball.feel} feel)
              </option>
            ))}
          </select>
        </div>

        {/* User's Balls List */}
        <div className="space-y-2">
          {userBalls.length === 0 && (
            <p className="text-text-muted text-center py-8">No balls in your bag. Add one above.</p>
          )}
          {userBalls.map(ball => (
            <div key={ball.id} className="flex justify-between items-center bg-background p-4 rounded-lg border border-border">
              <div>
                <span className="font-bold text-text-primary">{ball.display_name}</span>
                <div className="text-sm text-text-muted mt-1">
                  {ball.driver_spin_profile} spin • {ball.feel} feel • Compression: {ball.compression}
                </div>
              </div>
              <button
                onClick={() => removeBallFromBag(ball.id)}
                className="text-text-muted hover:text-red-400 p-1"
              >
                <Trash2 size={16}/>
              </button>
            </div>
          ))}
        </div>
      </Card>

      {/* Bag Analysis Section - Side by Side Layout */}
      {gapAnalysis.gaps.length > 0 && bag.length >= 2 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Gapping Analysis */}
          <Card padding="p-4">
            <h3 className="text-lg font-bold mb-3 flex gap-2 items-center">
              <Target className="text-success" size={18} /> Gapping Analysis
            </h3>

            {/* Coverage Stats - Compact */}
            <div className="flex gap-3 mb-3 text-sm">
              <div className="bg-page px-3 py-1.5 rounded">
                <span className="text-text-muted">Clubs:</span> <span className="font-bold text-text-primary">{gapAnalysis.total_clubs}</span>
              </div>
              <div className="bg-page px-3 py-1.5 rounded">
                <span className="text-text-muted">Range:</span> <span className="font-bold text-success">{gapAnalysis.coverage?.shortest || 0}</span>
                <span className="text-text-muted">-</span>
                <span className="font-bold text-primary">{gapAnalysis.coverage?.longest || 0}</span>
                <span className="text-text-muted text-xs ml-1">yds</span>
              </div>
            </div>

            {/* Compact Distance Chart */}
            <div className="space-y-0 mb-3">
              {(() => {
                const items = [];
                const maxDist = gapAnalysis.coverage?.longest || 300;

                // Suggest club type based on distance
                const suggestClub = (dist) => {
                  if (dist >= 260) return 'Driver';
                  if (dist >= 230) return '3-Wood';
                  if (dist >= 210) return '5-Wood';
                  if (dist >= 190) return 'Hybrid';
                  if (dist >= 170) return '4-Iron';
                  if (dist >= 160) return '5-Iron';
                  if (dist >= 150) return '6-Iron';
                  if (dist >= 140) return '7-Iron';
                  if (dist >= 130) return '8-Iron';
                  if (dist >= 120) return '9-Iron';
                  if (dist >= 100) return 'PW';
                  if (dist >= 80) return 'GW';
                  return 'SW';
                };

                gapAnalysis.gaps.forEach((gap, i) => {
                  // Add first club
                  if (i === 0) {
                    items.push({ type: 'club', name: gap.from_club || 'Club', distance: gap.from_distance || 0 });
                  }

                  // Add gap suggestion if gap is large (>20 yards)
                  if (gap.status === 'large' && gap.gap > 20) {
                    const midDistance = Math.round(((gap.from_distance || 0) + (gap.to_distance || 0)) / 2);
                    items.push({ type: 'gap', suggestedClub: suggestClub(midDistance), distance: midDistance });
                  }

                  // Add the club
                  items.push({ type: 'club', name: gap.to_club || 'Club', distance: gap.to_distance || 0 });
                });

                return items.map((item, i) => (
                  item.type === 'club' ? (
                    <div key={i} className="flex items-center gap-2 py-1">
                      <div className="w-24 flex-shrink-0 truncate text-xs text-text-primary">{item.name}</div>
                      <div className="flex-1">
                        <div className="h-4 bg-secondary/60 rounded-r flex items-center justify-end pr-1" style={{ width: `${(item.distance / maxDist) * 100}%` }}>
                          <span className="text-[10px] font-mono text-text-primary">{item.distance}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div key={i} className="flex items-center gap-2 py-1">
                      <div className="w-24 flex-shrink-0 truncate text-xs text-primary italic">+ {item.suggestedClub}?</div>
                      <div className="flex-1">
                        <div className="h-4 bg-primary/40 border border-dashed border-primary rounded-r flex items-center justify-end pr-1" style={{ width: `${(item.distance / maxDist) * 100}%` }}>
                          <span className="text-[10px] font-mono text-primary-light">~{item.distance}</span>
                        </div>
                      </div>
                    </div>
                  )
                ));
              })()}
            </div>

            {/* Loft Conflict Warnings */}
            {gapAnalysis.loft_conflicts?.length > 0 && (
              <div className="space-y-2 border-t border-border pt-2 mb-2">
                {gapAnalysis.loft_conflicts.map((conflict, i) => (
                  <div key={i} className="bg-status-warning/10 border border-status-warning/50 rounded-lg p-2.5 text-xs">
                    <div className="flex items-start gap-1.5 text-status-warning font-medium">
                      <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                      <span>Loft Conflict</span>
                    </div>
                    <p className="text-text-secondary mt-1 ml-4">
                      {conflict.club_a} ({conflict.club_a_loft}&deg;) and {conflict.club_b} ({conflict.club_b_loft}&deg;) cover the same distance. Consider replacing one.
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Warnings & Recommendations - Compact */}
            {(gapAnalysis.warnings.filter(w => !w.startsWith('Loft Conflict')).length > 0 || gapAnalysis.recommendations?.length > 0) && (
              <div className="text-xs space-y-2 border-t border-border pt-2">
                {gapAnalysis.warnings.filter(w => !w.startsWith('Loft Conflict')).map((w, i) => (
                  <div key={i} className="text-status-warning flex items-start gap-1">
                    <AlertTriangle size={10} className="mt-0.5 flex-shrink-0" /> {w}
                  </div>
                ))}
                {gapAnalysis.recommendations?.map((r, i) => (
                  <div key={i} className="text-status-success flex items-start gap-1">
                    <CheckCircle size={10} className="mt-0.5 flex-shrink-0" /> {typeof r === 'string' ? r : r.message}
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Dispersion Cloud */}
          <Card className="p-4">
            <h3 className="text-lg font-heading font-bold mb-3 flex gap-2 items-center">
              <BarChart3 className="text-accent" size={18} /> Dispersion Cloud
            </h3>
            <p className="text-xs text-text-secondary mb-2">Overlapping ellipses = potential redundant clubs</p>
            <BagCloudChart clubs={bag} />
          </Card>
        </div>
      )}

      {/* Fallback: Show only one section if only gaps or only 2+ clubs */}
      {gapAnalysis.gaps.length > 0 && bag.length < 2 && (
        <Card padding="p-4">
          <h3 className="text-lg font-bold mb-3 flex gap-2 items-center">
            <Target className="text-success" size={18} /> Gapping Analysis
          </h3>
          <p className="text-text-muted text-sm">Add more clubs to see full analysis.</p>
        </Card>
      )}

      {/* Import Review Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl border border-border max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-border flex justify-between items-center">
              <div>
                <h3 className="font-bold text-text-primary text-lg">Review Imported Clubs</h3>
                <p className="text-xs text-text-muted">
                  {importSource !== 'unknown' ? `Detected: ${importSource}` : 'Screenshot import'} · {importedClubs.filter(c => c.include).length} clubs selected
                </p>
              </div>
              <button onClick={() => setShowImportModal(false)} className="text-text-muted hover:text-text-primary text-xl">&times;</button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[50vh] space-y-2">
              {importedClubs.map((club, i) => (
                <div key={i} className={`flex items-center gap-3 p-3 rounded-lg border ${club.include ? 'bg-surface-light border-border' : 'bg-background border-border opacity-50'}`}>
                  <input type="checkbox" checked={club.include} onChange={e => updateImportedClub(i, 'include', e.target.checked)} className="rounded bg-zinc-700 border-zinc-600" />
                  <div className="flex-1">
                    <input
                      value={club.name}
                      onChange={e => updateImportedClub(i, 'name', e.target.value)}
                      className="bg-transparent text-text-primary font-medium w-full outline-none border-b border-transparent focus:border-blue-500"
                    />
                    <div className="flex items-center gap-2 mt-1">
                      <select value={club.type} onChange={e => updateImportedClub(i, 'type', e.target.value)} className="bg-surface text-xs text-text-secondary border border-border rounded px-2 py-1">
                        <option>Driver</option><option>Wood</option><option>Hybrid</option><option>Iron</option><option>Wedge</option><option>Putter</option>
                      </select>
                      {club.matched && <span className="text-xs text-emerald-400">✓ Matched</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      value={club.avg_distance || ''}
                      onChange={e => updateImportedClub(i, 'avg_distance', e.target.value ? parseInt(e.target.value) : null)}
                      placeholder="Yds"
                      className="w-20 bg-surface border border-border rounded px-2 py-1 text-text-primary text-right"
                    />
                    <span className="text-xs text-text-muted">yds</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-border flex justify-end gap-3">
              <button onClick={() => setShowImportModal(false)} className="px-4 py-2 text-text-secondary hover:text-text-primary">Cancel</button>
              <button
                onClick={confirmImport}
                disabled={importing || importedClubs.filter(c => c.include).length === 0}
                className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-700 text-text-primary px-4 py-2 rounded-lg flex items-center gap-2"
              >
                {importing ? <RefreshCw className="animate-spin" size={14} /> : <Plus size={14} />}
                Add {importedClubs.filter(c => c.include).length} Clubs
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Club Edit Modal */}
      {editingClub && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditingClub(null)}>
          <div className="bg-surface rounded-lg p-6 max-w-md w-full mx-4 border border-border" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-text-primary mb-4 flex items-center gap-2">
              <Wrench size={20} className="text-primary" />
              Edit Club Settings
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-text-secondary mb-1">Club Name</label>
                <div className="text-text-primary font-medium">{editingClub.name}</div>
                {editingClub.matched && (
                  <div className="flex items-center gap-1 text-xs text-success mt-1">
                    <CheckCircle size={12} />
                    Matched with inventory
                  </div>
                )}
                {enrichmentInfo && (
                  <div className="mt-2 bg-surface-light rounded-lg p-3 text-xs space-y-1">
                    <div className="text-primary font-medium">{enrichmentInfo.canonical_name}</div>
                    {enrichmentInfo.year && <div className="text-text-muted">Year: {enrichmentInfo.year}</div>}
                    {enrichmentInfo.match_confidence != null && (
                      <div className="text-text-muted">Confidence: {Math.round(enrichmentInfo.match_confidence * 100)}%</div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm text-text-secondary mb-2">Loft</label>
                <input
                  type="number"
                  step="0.5"
                  value={editForm.loft}
                  onChange={(e) => setEditForm({ ...editForm, loft: parseFloat(e.target.value) })}
                  className="w-full bg-surface-light border border-border rounded-lg px-3 py-2 text-text-primary"
                />
              </div>

              <div>
                <label className="block text-sm text-text-secondary mb-2">Lie Angle</label>
                <select
                  value={editForm.lie}
                  onChange={(e) => setEditForm({ ...editForm, lie: e.target.value })}
                  className="w-full bg-surface-light border border-border rounded-lg px-3 py-2 text-text-primary"
                  style={{ colorScheme: 'dark' }}
                >
                  <option value="flat" className="bg-surface">Flat (-2°)</option>
                  <option value="standard" className="bg-surface">Standard</option>
                  <option value="upright" className="bg-surface">Upright (+2°)</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setEditingClub(null)}
                  className="flex-1 px-4 py-2 bg-surface-light hover:bg-zinc-700 text-text-primary rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    updateClub(editingClub.id, {
                      loft: editForm.loft,
                      lie_angle: editForm.lie,
                    });
                    setEditingClub(null);
                  }}
                  className="flex-1 px-4 py-2 bg-primary hover:bg-primary/90 text-text-primary rounded-lg transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
